import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Loader2, ScanLine, Users, CheckCircle2 } from "lucide-react";
import { api } from "./api";

const input = "w-full border border-white/15 bg-transparent px-4 py-3 text-sm outline-none focus:border-gold";

export function OrganizerPage() {
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({});
  const load = async () => {
    const r = await api.get("/organizer/events");
    setEvents(r.data);
    const s = {};
    await Promise.all(r.data.map(async (ev) => {
      try { s[ev.id] = (await api.get(`/organizer/events/${ev.id}/stats`)).data; } catch {}
    }));
    setStats(s);
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-oswald text-4xl font-700 uppercase tracking-tighter">Organizer</h1>
        <Link to="/scan" data-testid="scan-link" className="flex items-center gap-2 border border-gold px-4 py-2.5 text-gold">
          <ScanLine size={16} /><span className="font-mono text-[10px] uppercase tracking-[0.2em]">Scan</span>
        </Link>
      </div>

      <CreateEvent onDone={load} />

      <h2 className="mb-4 mt-10 font-mono text-[11px] uppercase tracking-[0.3em] text-gold">/ Your Events</h2>
      <div className="grid gap-3">
        {events.map((ev) => (
          <div key={ev.id} data-testid={`org-event-${ev.id}`} className="border border-white/10 bg-ink-900/60 p-5">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-oswald text-xl font-600 uppercase tracking-tight">{ev.title}</h3>
                <p className="text-sm text-zinc-400">{new Date(ev.date).toLocaleDateString()}</p>
              </div>
              <button data-testid={`del-event-${ev.id}`} onClick={async () => { await api.delete(`/events/${ev.id}`); load(); }} className="text-zinc-500 hover:text-red-400"><Trash2 size={18} /></button>
            </div>
            <div className="mt-4 flex gap-6 text-sm">
              <span className="flex items-center gap-1.5 text-zinc-300"><Users size={14} className="text-gold" /> {stats[ev.id]?.sold ?? 0} sold</span>
              <span className="flex items-center gap-1.5 text-zinc-300"><CheckCircle2 size={14} className="text-gold" /> {stats[ev.id]?.checked_in ?? 0} in</span>
              <Link to={`/gallery`} className="ml-auto font-mono text-[10px] uppercase tracking-[0.2em] text-gold">+ Photos</Link>
            </div>
          </div>
        ))}
        {events.length === 0 && <p className="text-zinc-500">No events yet — create your first above.</p>}
      </div>
    </div>
  );
}

function CreateEvent({ onDone }) {
  const [f, setF] = useState({ title: "", date: "", location: "", image: "", description: "" });
  const [tt, setTt] = useState([{ name: "General", price: "25" }]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title || !f.date) { setMsg("Title and date required"); return; }
    setBusy(true); setMsg("");
    try {
      await api.post("/events", {
        ...f,
        date: new Date(f.date).toISOString(),
        ticket_types: tt.filter((t) => t.name).map((t) => ({ name: t.name, price: parseFloat(t.price) || 0, quantity: 100 })),
        published: true,
      });
      setF({ title: "", date: "", location: "", image: "", description: "" });
      setTt([{ name: "General", price: "25" }]); setMsg("Event created!"); onDone();
    } catch (e) { setMsg("Failed to create event"); }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} data-testid="create-event-form" className="border border-gold/30 bg-ink-900/40 p-5">
      <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.25em] text-gold">New Event</p>
      <div className="grid gap-3">
        <input data-testid="ev-title" className={input} placeholder="Event title" value={f.title} onChange={(e) => set("title", e.target.value)} />
        <input data-testid="ev-date" className={input} type="datetime-local" value={f.date} onChange={(e) => set("date", e.target.value)} />
        <input data-testid="ev-location" className={input} placeholder="Location" value={f.location} onChange={(e) => set("location", e.target.value)} />
        <input data-testid="ev-image" className={input} placeholder="Cover image URL (optional)" value={f.image} onChange={(e) => set("image", e.target.value)} />
        <textarea data-testid="ev-desc" className={input} rows={2} placeholder="Description" value={f.description} onChange={(e) => set("description", e.target.value)} />
        <div className="grid gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Ticket types</span>
          {tt.map((t, i) => (
            <div key={i} className="flex gap-2">
              <input data-testid={`tt-name-${i}`} className={input} placeholder="Name" value={t.name} onChange={(e) => setTt((p) => p.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
              <input data-testid={`tt-price-${i}`} className="w-28 border border-white/15 bg-transparent px-4 py-3 text-sm outline-none focus:border-gold" type="number" placeholder="$" value={t.price} onChange={(e) => setTt((p) => p.map((x, j) => j === i ? { ...x, price: e.target.value } : x))} />
            </div>
          ))}
          <button type="button" data-testid="add-tt" onClick={() => setTt((p) => [...p, { name: "", price: "" }])} className="flex items-center gap-1.5 text-gold"><Plus size={14} /><span className="font-mono text-[10px] uppercase tracking-[0.2em]">Add tier</span></button>
        </div>
        <button data-testid="create-event-submit" disabled={busy} className="flex items-center justify-center gap-2 bg-gold py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-ink-950 disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : "Publish Event"}
        </button>
        {msg && <p className="text-sm text-zinc-400" data-testid="create-event-msg">{msg}</p>}
      </div>
    </form>
  );
}
